import { useEffect } from 'react';
import { Check, FileSpreadsheet, MessageCircle, UserPlus } from 'lucide-react';
import { Button, cn } from '@hrms/ui';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';
import { SetupProgressBar } from '../panel/SetupProgressBar.js';
import { useAddTeamModal } from './AddTeamModalProvider.js';
import { preloadAddTeamModal } from './lazyAddYourTeamModal.js';
import {
  activationProgress,
  FIRST_STAFF_TARGET,
  useActivationStatus,
  type ActivationStepKey,
} from './activation.js';
import { useWhatsAppInvite, workspaceOrigin } from './useWhatsAppInvite.js';

function subtitle(teamSize: number, current: ActivationStepKey | null): string {
  if (teamSize === 0) {
    return "Your workspace is live, but your staff can't clock in yet because no employees are added.";
  }
  if (current === 'team') {
    const left = FIRST_STAFF_TARGET - teamSize;
    return `${teamSize} added. Add ${left} more so your team can start clocking in.`;
  }
  return 'Your team is in. Ask one of them to clock in from the phone app to check attendance works.';
}

/**
 * "Get your HRMS active" at the top of Home for whoever can add employees: workspace created, the
 * first three employees, the first mobile punch-in (dropped when attendance is not on the plan).
 * Its buttons open the shell's one Add your team modal (directly, or on its file picker) and share
 * a WhatsApp invite. Renders nothing while loading, on an error, for anyone else, and once every
 * step is done.
 */
export function ActivationCard() {
  const { me } = useAuthenticatedSession();
  const status = useActivationStatus();
  const addTeam = useAddTeamModal();
  const whatsApp = useWhatsAppInvite(me.person.orgSlug);
  const progress = status ? activationProgress(status.teamSize, status.punch) : null;
  const visible = addTeam !== null && progress !== null && progress.current !== null;

  useEffect(() => {
    if (visible) preloadAddTeamModal();
  }, [visible]);

  if (!visible || !status || !progress || !addTeam) return null;

  const host = new URL(workspaceOrigin(me.person.orgSlug)).host;
  const added = Math.min(status.teamSize, FIRST_STAFF_TARGET);
  const labels: Record<ActivationStepKey, string> = {
    workspace: `Workspace created (${host})`,
    team: `Add your first ${FIRST_STAFF_TARGET} employees`,
    punch: 'Test your first mobile punch-in',
  };

  return (
    <section
      aria-labelledby="activation-card-title"
      className="flex min-w-0 flex-col gap-4 rounded-2xl border border-border bg-surface p-4 shadow-xs sm:p-5"
    >
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 id="activation-card-title" className="font-display text-base font-extrabold text-fg">
            Get your HRMS active
          </h2>
          <p className="text-sm text-fg-muted">
            {progress.done} of {progress.total} steps completed
            <span className="ml-2 font-semibold tabular-nums text-fg">{progress.percent}%</span>
          </p>
        </div>
        <SetupProgressBar done={progress.done} total={progress.total} />
        <p className="text-sm text-fg-muted">{subtitle(status.teamSize, progress.current)}</p>
      </header>

      <ol className="flex flex-col gap-2">
        {progress.steps.map((step) => {
          const here = step.key === progress.current;
          return (
            <li
              key={step.key}
              data-testid={`activation-step-${step.key}`}
              data-done={step.done}
              className={cn(
                'flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-sm',
                here ? 'border-accent bg-accent-subtle' : 'border-border',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border',
                  step.done
                    ? 'border-success bg-success text-fg-on-accent'
                    : 'border-border-strong',
                )}
              >
                {step.done && <Check className="size-3.5" />}
              </span>
              <span
                className={cn(
                  'min-w-0 flex-1 break-words',
                  step.done ? 'text-fg-muted line-through' : 'font-medium text-fg',
                )}
              >
                {labels[step.key]}
                {step.key === 'team' && (
                  <span className="ml-1.5 tabular-nums text-fg-muted">
                    {added}/{FIRST_STAFF_TARGET}
                  </span>
                )}
              </span>
              <span className="sr-only">{step.done ? ', done' : ', not done'}</span>
              {here && (
                <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-fg-on-accent">
                  You are here
                </span>
              )}
            </li>
          );
        })}
      </ol>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button variant="primary" size="lg" onClick={() => addTeam.open()}>
          <UserPlus className="size-4" aria-hidden="true" />
          Add employees now
        </Button>
        <Button variant="secondary" size="lg" onClick={() => void whatsApp.invite()}>
          <MessageCircle className="size-4" aria-hidden="true" />
          Copy WhatsApp invite
        </Button>
        <Button variant="secondary" size="lg" onClick={() => addTeam.open({ upload: true })}>
          <FileSpreadsheet className="size-4" aria-hidden="true" />
          Upload Excel sheet
        </Button>
      </div>
      {whatsApp.dialog}
    </section>
  );
}
